
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Thermometer,
  Droplets,
  Wind,
  Eye,
  CloudSun,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface EnvironmentMetrics {
  temperature: number;
  humidity: number;
  windSpeed: number;
  windDirection: string;
  conditions: string;
  visibility?: number;
  pressure?: number;
  uvIndex?: number;
  lastUpdated?: Date;
}

interface EnvironmentDataProps {
  data: EnvironmentMetrics;
  onRefresh?: () => void;
}

export function EnvironmentData({ data, onRefresh }: EnvironmentDataProps) {
  const getTemperatureColor = (temp: number) => {
    if (temp >= 30) return 'text-red-600 bg-red-50';
    if (temp >= 20) return 'text-green-600 bg-green-50';
    if (temp >= 10) return 'text-blue-600 bg-blue-50';
    return 'text-blue-800 bg-blue-100';
  };

  const getHumidityColor = (humidity: number) => {
    if (humidity >= 80) return 'text-blue-600 bg-blue-50';
    if (humidity >= 60) return 'text-green-600 bg-green-50';
    if (humidity >= 40) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getWindColor = (speed: number) => {
    if (speed >= 20) return 'text-red-600 bg-red-50';
    if (speed >= 10) return 'text-yellow-600 bg-yellow-50';
    if (speed >= 5) return 'text-green-600 bg-green-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getConditionsIcon = (conditions: string) => {
    const icons = {
      'Clear': '☀️',
      'Partly Cloudy': '⛅',
      'Cloudy': '☁️',
      'Overcast': '☁️',
      'Light Rain': '🌦️',
      'Rain': '🌧️',
      'Heavy Rain': '⛈️',
      'Thunderstorm': '⛈️',
      'Fog': '🌫️',
      'Windy': '💨',
    };
    return icons[conditions as keyof typeof icons] || '🌤️';
  };

  const getPlayingConditions = () => {
    const temp = data.temperature;
    const humidity = data.humidity;
    const wind = data.windSpeed;
    
    if (temp < 5 || temp > 40 || humidity > 90 || wind > 25) {
      return { status: 'Poor', color: 'bg-red-500', description: 'Consider adjusting training' };
    }
    if (temp < 10 || temp > 35 || humidity > 80 || wind > 15) {
      return { status: 'Fair', color: 'bg-yellow-500', description: 'Monitor conditions closely' };
    }
    return { status: 'Excellent', color: 'bg-green-500', description: 'Ideal for training' };
  };

  const playingConditions = getPlayingConditions();

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <CloudSun className="w-5 h-5 mr-2" />
              Environment
            </CardTitle>
            <CardDescription>
              Current training conditions
            </CardDescription>
          </div>
          
          {onRefresh && (
            <Button variant="ghost" size="sm" onClick={onRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Current Conditions */}
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-3xl mb-2">{getConditionsIcon(data.conditions)}</div>
          <p className="font-medium text-sm">{data.conditions}</p>
          <Badge 
            className={`mt-2 ${playingConditions.color} text-white border-none`}
          >
            {playingConditions.status} Conditions
          </Badge>
          <p className="text-xs text-muted-foreground mt-1">
            {playingConditions.description}
          </p>
        </div>

        {/* Temperature */}
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${getTemperatureColor(data.temperature)}`}>
              <Thermometer className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-medium">Temperature</p>
              <p className="text-xs text-muted-foreground">Current temp</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold">{data.temperature}°C</p>
            <p className="text-xs text-muted-foreground">
              {((data.temperature * 9/5) + 32).toFixed(0)}°F
            </p>
          </div>
        </div>

        {/* Humidity */}
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${getHumidityColor(data.humidity)}`}>
              <Droplets className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-medium">Humidity</p>
              <p className="text-xs text-muted-foreground">Moisture level</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold">{data.humidity}%</p>
          </div>
        </div>

        {/* Wind */}
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${getWindColor(data.windSpeed)}`}>
              <Wind className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-medium">Wind</p>
              <p className="text-xs text-muted-foreground">{data.windDirection}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold">{data.windSpeed} km/h</p>
            <p className="text-xs text-muted-foreground">
              {(data.windSpeed * 0.621371).toFixed(1)} mph
            </p>
          </div>
        </div>

        {/* Additional Metrics */}
        {(data.visibility || data.pressure || data.uvIndex) && (
          <div className="grid grid-cols-1 gap-2 pt-2 border-t">
            <h4 className="text-sm font-medium text-muted-foreground">Additional Info</h4>
            
            {data.visibility && (
              <div className="flex justify-between text-xs">
                <span className="flex items-center">
                  <Eye className="w-3 h-3 mr-1" />
                  Visibility
                </span>
                <span className="font-medium">{data.visibility} km</span>
              </div>
            )}
            
            {data.pressure && (
              <div className="flex justify-between text-xs">
                <span>Pressure</span>
                <span className="font-medium">{data.pressure} hPa</span>
              </div>
            )}
            
            {data.uvIndex && (
              <div className="flex justify-between text-xs">
                <span>UV Index</span>
                <span className="font-medium">{data.uvIndex}/10</span>
              </div>
            )}
          </div>
        )}

        {/* Last Updated */}
        {data.lastUpdated && (
          <div className="text-center text-xs text-muted-foreground pt-2 border-t">
            Updated: {data.lastUpdated.toLocaleTimeString()}
          </div>
        )}

        {/* Training Impact */}
        <div className="bg-blue-50 p-3 rounded-lg">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Training Impact</h4>
          <div className="space-y-1 text-xs text-blue-700">
            {data.windSpeed > 15 && (
              <p>• Strong wind may affect ball trajectory</p>
            )}
            {data.humidity > 80 && (
              <p>• High humidity may affect player endurance</p>
            )}
            {data.temperature > 30 && (
              <p>• High temperature - ensure hydration breaks</p>
            )}
            {data.temperature < 10 && (
              <p>• Cool conditions - longer warm-up recommended</p>
            )}
            {data.windSpeed <= 10 && data.humidity <= 70 && data.temperature >= 15 && data.temperature <= 28 && (
              <p>• Excellent conditions for skill development</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}